import OrgPageTemplate from '@admin/components/organization/OrgPageTemplate';
import useBreadcrumbs from '@admin/hooks/useAdminBreadcrumbs';
import styled from '@emotion/styled';
import PageHeader from '@shared/components/core/headers/PageHeader';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import ProfileConflictIndicators from '@shared/components/profile/ProfileConflictIndicators';
import { ProfileOptionsPopover } from '@shared/components/profile/ProfilesOptionPopover';
import { DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import { publish } from '@shared/hooks/helpers/EventCallback';
import { extractRoleProperties } from '@shared/hooks/helpers/OrganizationalHelpers';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import OrganizationContainer from '@shared/hooks/state/OrganizationContainer';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches, useParams } from 'react-router';

export default function ProfileLayout() {
    const { t } = useTranslation();
    const { items } = useBreadcrumbs();
    const { profileId, organizationId } = useParams();
    const { getOrgAvailableCustomControls, orgAvailableCustomControlsRef } = OrganizationContainer.useContainer();
    const matches = useMatches();
    const editProfileContainer = EditProfileContainer.useContainer();
    const {
        currentOrg,
        membership,
        isLoading: orgLoading,
        canEditOrganizationProfiles,
    } = useCurrentOrgDetailsFetching();
    const [isInitialized, setIsInitialized] = useState(false);

    const isNew = profileId?.toLowerCase() === 'new';
    const myRole = membership ? extractRoleProperties(membership) : undefined;
    const canManage = myRole?.canEditOrganizationProfiles || myRole?.isAdmin;

    // Get title and description from route handle, with fallbacks
    const currentMatch = matches[matches.length - 1];
    const handle = currentMatch?.handle as { title?: string; description?: string; showActions?: boolean } | undefined;

    const pageTitle = handle?.title
        ? t(handle.title)
        : isNew
          ? t('common:profile.createTitle')
          : editProfileContainer.name || t('common:profile.edit');

    const pageDescription = handle?.description ? t(handle.description) : undefined;

    useEffect(() => {
        // Don't update if user has unsaved changes (e.g., copying a profile)
        // This check MUST happen before any other logic
        if (editProfileContainer.hasChangesRef.current) {
            setIsInitialized(true);
            return;
        }

        const getDeviceTypeCustomControls = async () => {
            if (!currentOrg?.id) return;
            const result = await getOrgAvailableCustomControls(
                currentOrg.id,
                editProfileContainer.defaultLocaleRef.current
            );
            if (!result) {
                publish('alertError', t('common:errors.loadCustomControls'));
            } else {
                editProfileContainer.setAvailableCustomControls(orgAvailableCustomControlsRef.current);
            }
            console.log('Available custom controls:', orgAvailableCustomControlsRef.current);
        };

        const initializeProfile = async () => {
            if (orgLoading || !currentOrg?.id) return;
            editProfileContainer.organizationIdRef.current = currentOrg.id;

            if (isNew || !profileId) {
                editProfileContainer.setDefaultProfile(DefaultVoiceLocale);
                setIsInitialized(true);
            } else if (profileId) {
                const success = await editProfileContainer.loadOrgPreview(profileId);
                editProfileContainer.setIsPreview(false);
                setIsInitialized(true);
            } else {
                publish('alertError', t('common:errors.loadProfile'));
            }
        };

        setIsInitialized(false);
        initializeProfile();
        getDeviceTypeCustomControls();

        return () => {
            // Only reset if there are no unsaved changes (e.g., not in the middle of copying)
            if (!editProfileContainer.hasChangesRef.current) {
                editProfileContainer.setDefaultProfile(DefaultVoiceLocale);
                editProfileContainer.setHasChanges(false);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId, isNew, currentOrg?.id, orgLoading]);

    if (!isInitialized) return <SpacedLoader />;
    return (
        <OrgPageTemplate>
            <PageHeader title={editProfileContainer.name || pageTitle} description={pageDescription} items={items}>
                {handle?.showActions && profileId && canEditOrganizationProfiles && (
                    <HeaderActions>
                        <ProfileOptionsPopover disabled={!canManage || editProfileContainer.isPreview} />
                    </HeaderActions>
                )}
            </PageHeader>
            <ProfileConflictIndicators errorOnly />
            <Outlet />
        </OrgPageTemplate>
    );
}

const HeaderActions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
`;
